/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { UnityRootState } from "../UnityAppStore";
import type { TToastMessageState } from "./toastMessage.types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "toastMessageSlice";

const INITIAL_STATE: TToastMessageState = {
  isVisible: false,
  message: ""
};

const toastMessageSlice = createSlice({
  name: "toastMessage",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Show the toast with the given message text.
     * Setting the message on every call (even when already visible) allows
     * the container to restart its auto-hide timer for the latest message.
     */
    showToastMessage(
      state: TToastMessageState,
      action: PayloadAction<string>
    ): void {
      state.isVisible = true;
      state.message = action.payload;
    },

    /**
     * Hide the toast. The message text is intentionally left in place so
     * exit animations can reference it; use `resetToastMessage` to clear it.
     */
    hideToastMessage(state: TToastMessageState): void {
      state.isVisible = false;
    },

    /**
     * Clear the message text. Used to discard a stale message on mount.
     */
    resetToastMessage(state: TToastMessageState): void {
      state.message = "";
    }
  }
});

/* ── Actions ──────────────────────────────────────────────────────────── */
export const { showToastMessage, hideToastMessage, resetToastMessage } =
  toastMessageSlice.actions;

/* ── Reducer ──────────────────────────────────────────────────────────── */
export const toastMessageReducer = toastMessageSlice.reducer;

/* ── Selectors ────────────────────────────────────────────────────────── */
export const selectIsToastMessageVisible = (state: UnityRootState): boolean =>
  state.toastMessage.isVisible;

export const selectToastMessageText = (state: UnityRootState): string =>
  state.toastMessage.message;
